import React, { useState } from 'react';
import cx from 'classnames';

import styles from './styles.module.scss';

const RequiredDocs = React.forwardRef((props, ref) => {
  const [firstTableExpanded, setFirstTableExpanded] = useState(false);
  const [secondTableExpanded, setSecondTableExpanded] = useState(false);

  return (
    <section className={styles.section} ref={ref}>
      <div className="container">
        <h2 className={styles.header}>What to prepare before you apply</h2>
        <p className={styles.intro}>
          Make sure you have the documents required for your business type.
        </p>
        <button
          type="button"
          className={styles.accordionButton}
          aria-controls="corporate-table"
          aria-expanded={firstTableExpanded}
          id="corporate-toggle"
          onClick={() => {
            setFirstTableExpanded(state => !state);
          }}
        >
          <div
            className={cx(styles.accordion, {
              [styles.accordionExpanded]: firstTableExpanded,
            })}
          >
            <p>If your business structure is any of the following:</p>
            <p>
              Corporation, Professional Corporation, General Partnership, LLC,
              Nonprofit
            </p>
          </div>
        </button>
        <div
          className={cx(styles.accordionContent, {
            [styles.accordionContentExpanded]: firstTableExpanded,
          })}
        >
          <div
            className={styles.table}
            role="region"
            aria-labelledby="corporate-toggle"
            aria-hidden={!firstTableExpanded}
            id="corporate-table"
          >
            <div className={styles.column}>
              <div className={styles.colHeader} />
              <div className={styles.rowHeader}>
                All businesses of this type should provide the following
              </div>
              <div className={styles.item}>
                <ul>
                  <li>Color copy of government issued ID (front and back)</li>
                  <li>Organizational Documents*</li>
                  <li>
                    <a
                      href="https://www.irs.gov/pub/irs-pdf/fw3_19.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      2019 IRS Form W-3
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.irs.gov/pub/irs-pdf/f940.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      2019 IRS Form 940
                    </a>
                  </li>
                </ul>
              </div>
              <div className={styles.rowHeader}>
                If you have employees outside the US
              </div>
              <div className={styles.item}>
                <a
                  href="https://www.irs.gov/pub/irs-pdf/fw2_19.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  W-2s
                </a>{' '}
                for employees living abroad
              </div>
              <div className={styles.rowHeader}>
                If you have employees making over $100,000
              </div>
              <div className={styles.item}>
                <a
                  href="https://www.irs.gov/pub/irs-pdf/fw2_19.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  W-2s
                </a>{' '}
                for employees making over $100K
              </div>
            </div>
            <div className={styles.column} />
          </div>
          <p className={styles.notes}>
            *Examples of Organizational Documents include: Articles of
            Incorporation, Certificate of Existence, Certificate of
            Organization, State LLC Agreement, Certificate of Formation or
            Articles of Information
          </p>
        </div>
        <button
          type="button"
          className={styles.accordionButton}
          aria-controls="independent-table"
          aria-expanded={secondTableExpanded}
          id="independent-toggle"
          onClick={() => {
            setSecondTableExpanded(state => !state);
          }}
        >
          <div
            className={cx(styles.accordion, {
              [styles.accordionExpanded]: secondTableExpanded,
            })}
          >
            <p>If your business structure is any of the following:</p>
            <p>
              Sole Proprietorship, Independent Contractor, Self-employed, Single
              member LLC
            </p>
          </div>
        </button>
        <div
          className={cx(styles.accordionContent, {
            [styles.accordionContentExpanded]: secondTableExpanded,
          })}
        >
          <div
            className={styles.table}
            role="region"
            aria-labelledby="independent-toggle"
            aria-hidden={!secondTableExpanded}
            id="independent-table"
          >
            <div className={styles.column}>
              <h3 className={styles.colHeader}>If you have employees</h3>
              <div className={styles.rowHeader}>
                All businesses of this type should provide the following
              </div>
              <div className={cx(styles.item, styles.itemExtraLarge)}>
                <ul>
                  <li>Color copy of government issued ID (front and back)</li>
                  <li>
                    <a
                      href="https://www.irs.gov/pub/irs-pdf/f1040sc.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      2019 1040 Schedule C**
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.irs.gov/pub/irs-pdf/fw3_19.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      2019 IRS Form W-3
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.irs.gov/pub/irs-pdf/f940.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      2019 IRS Form 940
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.irs.gov/pub/irs-pdf/fw2_19.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      W-2s
                    </a>{' '}
                    for any employees earning more than $100,000
                  </li>
                  <li>Payroll statement covering 2/15/2020</li>
                </ul>
              </div>
            </div>
            <div className={styles.column}>
              <h3 className={styles.colHeader}>If you do not have employees</h3>
              <div className={styles.rowHeader}>
                All businesses of this type should provide the following
              </div>
              <div className={styles.item}>
                <ul>
                  <li>Color copy of government issued ID (front and back)</li>
                  <li>
                    <a
                      href="https://www.irs.gov/pub/irs-pdf/f1040sc.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      2019 1040 Schedule C**
                    </a>
                  </li>
                  <li>Substantiation of self employment</li>
                  <ul>
                    <li>
                      <a
                        href="https://www.irs.gov/pub/irs-prior/f1099msc--2019.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        1099-MISC
                      </a>{' '}
                      for Independent Contractors
                    </li>
                  </ul>
                  <li>
                    2020 invoice, bank statement or book of record to establish
                    you were operating effective 2/15/2020
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <p className={styles.notes}>
            **If you have not completed 1040 Schedule C for 2019, fill it out
            and compute the value for line 31. We cannot accept this form for
            previous years. If line 31 on the 1040 Schedule C is less than
            $2,400, your business is not eligible for a PPP loan.
          </p>
        </div>
      </div>
    </section>
  );
});

export default RequiredDocs;
